import { type EnemyKind, GROUND_POINTS, type GroundStats, scoreGround, waveSize } from "../../shared/ground";
import {
  aimPoint,
  type Delivery,
  type FlowEngine,
  type FlowEvent,
  type GunKind,
  type Report,
  type SensorKind,
} from "./graph";
import {
  ACOUSTIC_INFANTRY_RANGE,
  ATTACK_DRONE_ORBIT,
  ATTACK_DRONE_PERIOD_S,
  BASE,
  BASE_HP,
  CAMERA_FOV,
  distToRoad,
  DRONE_ORBIT,
  DRONE_PERIOD_S,
  ENEMIES,
  FIXED_GUNS,
  HEIGHT,
  MISSILE_SPEED,
  ORDER_STALE_S,
  type Point,
  type RoadId,
  ROADS,
  SEEKER_RADIUS,
  SELL_REFUND,
  SENSORS,
  START_CREDITS,
  WAVE_BREAK_S,
  WEAPONS,
  type WeaponSpec,
  WIDTH,
} from "./world";

interface Enemy {
  id: number;
  kind: EnemyKind;
  hp: number;
  maxHp: number;
  speed: number;
  path: Point[];
  seg: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  heading: number;
}

export interface Sensor {
  id: string;
  kind: SensorKind;
  label: string;
  /** Placement point; a drone orbits it. */
  x: number;
  y: number;
  /** Camera facing (radians). */
  dir: number;
  phase: number;
  lastReport: Map<number, number>;
}

export interface Gun {
  id: string;
  kind: GunKind;
  label: string;
  /** Emplacement, or the point an attack drone orbits. */
  x: number;
  y: number;
  /** The base's starting guns can't be sold. */
  fixed: boolean;
  phase: number;
  nextFire: number;
  angle: number;
  lastShot: number;
  orders: Map<number, { report: Report; receivedAt: number }>;
}

/** Anything the player can buy and place on the map. */
export type PlaceKind = SensorKind | GunKind;

export interface Placed {
  id: string;
  kind: PlaceKind;
  label: string;
}

export function isSensorKind(k: PlaceKind): k is SensorKind {
  return k in SENSORS;
}

function specOf(k: PlaceKind): { label: string; cost: number } {
  return isSensorKind(k) ? SENSORS[k] : WEAPONS[k];
}

interface Shell {
  gun: WeaponSpec;
  from: Point;
  to: Point;
  launchedAt: number;
}

interface Missile {
  x: number;
  y: number;
  /** Enemy the seeker locked on to at launch, if any. */
  targetId: number | null;
  /** Where it flies when it has no lock. */
  to: Point;
}

interface Marker {
  x: number;
  y: number;
  error: number;
  kind: SensorKind;
  at: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  born: number;
  life: number;
  color: string;
}

interface Popup {
  x: number;
  y: number;
  text: string;
  color: string;
  at: number;
}

export type GroundPhase = "build" | "wave" | "break" | "over";

export interface GroundHud {
  phase: GroundPhase;
  wave: number;
  hp: number;
  credits: number;
  score: number;
  breakLeft: number;
}

export interface GroundEvents {
  onHud(hud: GroundHud): void;
  onFlow(event: FlowEvent): void;
  onPlaced(item: Placed): void;
  onRemoved(id: string): void;
  onSelect(item: Placed | null, screen: { x: number; y: number } | null): void;
  onMessage(text: string): void;
  onGameOver(stats: GroundStats): void;
}

const SENSOR_COLORS: Record<SensorKind, string> = {
  acoustic: "#5fd4ff",
  camera: "#ffd35f",
  drone: "#c99bff",
};

const rand = (a: number, b: number) => a + Math.random() * (b - a);

function emptyStats(): GroundStats {
  return { infantryKilled: 0, vehiclesKilled: 0, armorKilled: 0, wavesCleared: 0, shellsFired: 0 };
}

export class GroundGame {
  private ctx: CanvasRenderingContext2D;
  private terrain: HTMLCanvasElement;
  private time = 0;
  private lastFrame = 0;

  private phase: GroundPhase = "build";
  private wave = 0;
  private hp = BASE_HP;
  private credits = START_CREDITS;
  private stats = emptyStats();
  private breakEnds = 0;

  private enemies: Enemy[] = [];
  private spawnQueue: { at: number; kind: EnemyKind; road: RoadId }[] = [];
  private sensors: Sensor[] = [];
  private guns: Gun[] = FIXED_GUNS.map((g) => ({
    id: g.id,
    kind: g.kind,
    label: g.label,
    x: g.pos.x,
    y: g.pos.y,
    fixed: true,
    phase: 0,
    nextFire: 0,
    angle: -Math.PI / 2,
    lastShot: -1,
    orders: new Map(),
  }));
  private shells: Shell[] = [];
  private missiles: Missile[] = [];
  private markers: Marker[] = [];
  private particles: Particle[] = [];
  private popups: Popup[] = [];
  private craters: { x: number; y: number; r: number }[] = [];
  private pendingDeliveries: Delivery[] = [];
  private pendingEvents: FlowEvent[] = [];

  private nextEnemyId = 1;
  private nextPlacedNum = 1;
  private placing: PlaceKind | null = null;
  private hover: Point | null = null;
  private selected: Sensor | Gun | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private flow: FlowEngine,
    private events: GroundEvents,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D is not supported");
    this.ctx = ctx;
    this.terrain = paintTerrain();
    this.resize();
    window.addEventListener("resize", () => this.resize());
    canvas.addEventListener("pointermove", (e) => (this.hover = this.toWorld(e)));
    canvas.addEventListener("pointerleave", () => (this.hover = null));
    canvas.addEventListener("click", (e) => this.click(this.toWorld(e), e));
    requestAnimationFrame((t) => this.frame(t));
  }

  // ---------------------------------------------------------------- public controls

  /** Current sim time (s). */
  get now(): number {
    return this.time;
  }

  get started(): boolean {
    return this.phase !== "build";
  }

  get credit(): number {
    return this.credits;
  }

  /** Clears the field, every sensor and every bought gun for a fresh game. */
  reset(): void {
    for (const s of this.sensors) this.events.onRemoved(s.id);
    for (const g of this.guns) if (!g.fixed) this.events.onRemoved(g.id);
    this.sensors = [];
    this.guns = this.guns.filter((g) => g.fixed);
    for (const g of this.guns) g.nextFire = 0;
    this.enemies = [];
    this.spawnQueue = [];
    this.shells = [];
    this.missiles = [];
    this.markers = [];
    this.particles = [];
    this.popups = [];
    this.craters = [];
    this.pendingDeliveries = [];
    this.pendingEvents = [];
    for (const g of this.guns) g.orders.clear();
    this.flow.reset();
    this.phase = "build";
    this.wave = 0;
    this.hp = BASE_HP;
    this.credits = START_CREDITS;
    this.stats = emptyStats();
    this.select(null);
    this.setPlacing(null);
    this.emitHud();
  }

  start(): void {
    if (this.phase !== "build") return;
    this.startWave(1);
  }

  setPlacing(kind: PlaceKind | null): void {
    this.placing = kind;
    this.canvas.classList.toggle("placing", kind !== null);
    if (kind) this.select(null);
  }

  get placingKind(): PlaceKind | null {
    return this.placing;
  }

  rotateSelected(): void {
    if (this.selected?.kind === "camera") this.selected.dir += Math.PI / 4;
  }

  sellSelected(): void {
    const s = this.selected;
    if (!s || ("fixed" in s && s.fixed)) return;
    this.sensors = this.sensors.filter((x) => x !== s);
    this.guns = this.guns.filter((x) => x !== s);
    this.credits += Math.floor(specOf(s.kind).cost * SELL_REFUND);
    this.events.onRemoved(s.id);
    this.select(null);
    this.emitHud();
  }

  // ---------------------------------------------------------------- input

  private toWorld(e: MouseEvent): Point {
    const r = this.canvas.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * WIDTH, y: ((e.clientY - r.top) / r.height) * HEIGHT };
  }

  private click(p: Point, e: MouseEvent): void {
    if (this.phase === "over") return;
    if (this.placing) {
      this.place(this.placing, p);
      return;
    }
    const hit =
      this.sensors.find((s) => Math.hypot(s.x - p.x, s.y - p.y) < 16) ??
      this.guns.find((g) => !g.fixed && Math.hypot(g.x - p.x, g.y - p.y) < 16);
    if (hit) {
      if (hit === this.selected && hit.kind === "camera") (hit as Sensor).dir += Math.PI / 4;
      this.select(hit, { x: e.clientX, y: e.clientY });
    } else {
      this.select(null);
    }
  }

  private select(s: Sensor | Gun | null, screen: { x: number; y: number } | null = null): void {
    this.selected = s;
    this.events.onSelect(s ? { id: s.id, kind: s.kind, label: s.label } : null, screen);
  }

  private placementProblem(kind: PlaceKind, p: Point): string | null {
    const spec = specOf(kind);
    if (this.phase === "over") return "The game is over";
    if (this.credits < spec.cost) return `Not enough credits for ${article(spec.label)} ${spec.label.toLowerCase()}`;
    if (p.x < 10 || p.y < 10 || p.x > WIDTH - 10 || p.y > HEIGHT - 10) return "Too close to the edge";
    if (Math.hypot(p.x - BASE.x, p.y - BASE.y) < 36) return "Too close to the base";
    if (this.guns.some((g) => Math.hypot(p.x - g.x, p.y - g.y) < 30)) return "Too close to a gun";
    if (this.sensors.some((s) => Math.hypot(p.x - s.x, p.y - s.y) < 24)) return "Too close to a sensor";
    return null;
  }

  private place(kind: PlaceKind, p: Point): void {
    const problem = this.placementProblem(kind, p);
    if (problem) {
      this.events.onMessage(problem);
      return;
    }
    const spec = specOf(kind);
    const id = `placed-${this.nextPlacedNum++}`;
    const same = [...this.sensors, ...this.guns].filter((x) => x.kind === kind && !("fixed" in x && x.fixed)).length;
    const label = `${spec.label} ${same + 1}`;
    if (isSensorKind(kind)) {
      this.sensors.push({
        id,
        kind,
        label,
        x: p.x,
        y: p.y,
        dir: Math.PI, // cameras start facing west, where the enemy comes from
        phase: Math.random() * Math.PI * 2,
        lastReport: new Map(),
      });
    } else {
      this.guns.push({
        id,
        kind,
        label,
        x: p.x,
        y: p.y,
        fixed: false,
        phase: Math.random() * Math.PI * 2,
        nextFire: 0,
        angle: Math.PI,
        lastShot: -1,
        orders: new Map(),
      });
    }
    this.credits -= spec.cost;
    this.events.onPlaced({ id, kind, label });
    this.setPlacing(null);
    this.emitHud();
  }

  // ---------------------------------------------------------------- loop

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = WIDTH * dpr;
    this.canvas.height = HEIGHT * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private frame(now: number): void {
    if (this.canvas.offsetParent === null) {
      this.lastFrame = 0;
      requestAnimationFrame((t) => this.frame(t));
      return;
    }
    const dt = this.lastFrame ? Math.min((now - this.lastFrame) / 1000, 0.05) : 0;
    this.lastFrame = now;
    this.update(dt);
    this.render();
    requestAnimationFrame((t) => this.frame(t));
  }

  private update(dt: number): void {
    if (this.phase === "over" || dt === 0) return;
    this.time += dt;

    if (this.phase === "wave" || this.phase === "break") {
      this.spawn();
      this.moveEnemies(dt);
      this.sense();
      this.deliver();
      this.fireGuns();
      this.impacts();
      this.flyMissiles(dt);
      this.checkWave();
    }
    this.updateEffects(dt);
    this.emitHud();
  }

  private startWave(n: number): void {
    this.wave = n;
    this.phase = "wave";
    const size = waveSize(n);
    const kinds: EnemyKind[] = [
      ...Array<EnemyKind>(size.infantry).fill("infantry"),
      ...Array<EnemyKind>(size.vehicle).fill("vehicle"),
      ...Array<EnemyKind>(size.armor).fill("armor"),
    ].sort(() => Math.random() - 0.5);
    const gap = Math.max(0.6, 1.3 - n * 0.05);
    kinds.forEach((kind, i) => {
      const roads: RoadId[] = kind === "infantry" && n >= 3 ? ["north", "south", "trail"] : ["north", "south"];
      this.spawnQueue.push({ at: this.time + 0.5 + i * gap, kind, road: roads[Math.floor(Math.random() * roads.length)]! });
    });
    this.events.onMessage(`Wave ${n}: ${describeWave(size)}`);
  }

  private spawn(): void {
    while (this.spawnQueue.length && this.spawnQueue[0]!.at <= this.time) {
      const { kind, road } = this.spawnQueue.shift()!;
      const spec = ENEMIES[kind];
      const scale = 1 + 0.1 * (this.wave - 1);
      const path = ROADS[road].map((p) => ({ x: p.x, y: p.y + (road === "trail" ? 0 : rand(-6, 6)) }));
      this.enemies.push({
        id: this.nextEnemyId++,
        kind,
        hp: spec.hp * scale,
        maxHp: spec.hp * scale,
        speed: spec.speed * (1 + 0.03 * (this.wave - 1)),
        path,
        seg: 1,
        x: path[0]!.x,
        y: path[0]!.y,
        vx: 0,
        vy: 0,
        heading: 0,
      });
    }
  }

  private moveEnemies(dt: number): void {
    const keep: Enemy[] = [];
    for (const e of this.enemies) {
      let step = e.speed * dt;
      let arrived = false;
      while (step > 0) {
        const target = e.path[e.seg];
        if (!target) {
          arrived = true;
          break;
        }
        const d = Math.hypot(target.x - e.x, target.y - e.y);
        if (d <= step) {
          e.x = target.x;
          e.y = target.y;
          step -= d;
          e.seg++;
        } else {
          e.heading = Math.atan2(target.y - e.y, target.x - e.x);
          e.vx = Math.cos(e.heading) * e.speed;
          e.vy = Math.sin(e.heading) * e.speed;
          e.x += (e.vx / e.speed) * step;
          e.y += (e.vy / e.speed) * step;
          step = 0;
        }
      }
      if (arrived) {
        const dmg = ENEMIES[e.kind].baseDamage;
        this.hp = Math.max(0, this.hp - dmg);
        this.popup(BASE.x - 20, BASE.y - 30, `−${dmg} HP`, "#ff6b6b");
        this.burst(BASE.x - 10, BASE.y, 8);
        this.flow.forget(e.id);
        continue;
      }
      keep.push(e);
    }
    this.enemies = keep;
    if (this.hp <= 0) {
      this.phase = "over";
      this.select(null);
      this.setPlacing(null);
      this.emitHud();
      this.events.onGameOver({ ...this.stats });
    }
  }

  private sensorPos(s: Sensor): Point {
    if (s.kind !== "drone") return { x: s.x, y: s.y };
    const a = s.phase + (this.time * Math.PI * 2) / DRONE_PERIOD_S;
    return { x: s.x + Math.cos(a) * DRONE_ORBIT, y: s.y + Math.sin(a) * DRONE_ORBIT };
  }

  private sees(s: Sensor, e: Enemy): boolean {
    const p = this.sensorPos(s);
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    const spec = SENSORS[s.kind];
    if (s.kind === "acoustic") return d <= (e.kind === "infantry" ? ACOUSTIC_INFANTRY_RANGE : spec.range);
    if (s.kind === "camera") {
      if (d > spec.range) return false;
      let diff = Math.atan2(e.y - p.y, e.x - p.x) - s.dir;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      return Math.abs(diff) <= CAMERA_FOV / 2;
    }
    return d <= spec.range;
  }

  private sense(): void {
    for (const s of this.sensors) {
      const spec = SENSORS[s.kind];
      for (const e of this.enemies) {
        if (!this.sees(s, e)) continue;
        const last = s.lastReport.get(e.id) ?? -Infinity;
        if (this.time - last < spec.interval) continue;
        s.lastReport.set(e.id, this.time);

        const a = Math.random() * Math.PI * 2;
        const r = Math.sqrt(Math.random()) * spec.error;
        const report: Report = {
          trackId: e.id,
          sensorId: s.id,
          x: e.x + Math.cos(a) * r,
          y: e.y + Math.sin(a) * r,
          vx: spec.measuresVelocity ? e.vx : 0,
          vy: spec.measuresVelocity ? e.vy : 0,
          hasVelocity: spec.measuresVelocity,
          cls: spec.classifies ? e.kind : null,
          error: spec.error,
          truthX: e.x,
          truthY: e.y,
          seenAt: this.time,
          predictedAt: null,
        };
        this.markers.push({ x: report.x, y: report.y, error: spec.error, kind: s.kind, at: this.time });
        const { deliveries, events } = this.flow.inject(s.id, report);
        this.pendingDeliveries.push(...deliveries);
        this.pendingEvents.push(...events);
      }
    }
  }

  private deliver(): void {
    const still: Delivery[] = [];
    for (const d of this.pendingDeliveries) {
      if (d.at > this.time) {
        still.push(d);
        continue;
      }
      const gun = this.guns.find((g) => g.id === d.gun);
      const prev = gun?.orders.get(d.report.trackId);
      if (gun && (!prev || prev.report.seenAt <= d.report.seenAt)) {
        gun.orders.set(d.report.trackId, { report: d.report, receivedAt: d.at });
      }
    }
    this.pendingDeliveries = still;

    const later: FlowEvent[] = [];
    for (const ev of this.pendingEvents) {
      if (ev.at <= this.time) this.events.onFlow(ev);
      else later.push(ev);
    }
    this.pendingEvents = later;
  }

  /** Where a gun fires from: its emplacement, or an attack drone's current spot on its orbit. */
  private gunPos(g: Gun): Point {
    if (g.kind !== "attackDrone") return { x: g.x, y: g.y };
    const a = g.phase + (this.time * Math.PI * 2) / ATTACK_DRONE_PERIOD_S;
    return { x: g.x + Math.cos(a) * ATTACK_DRONE_ORBIT, y: g.y + Math.sin(a) * ATTACK_DRONE_ORBIT };
  }

  private fireGuns(): void {
    for (const g of this.guns) {
      for (const [id, o] of g.orders) if (this.time - o.receivedAt > ORDER_STALE_S) g.orders.delete(id);
      if (this.time < g.nextFire || g.orders.size === 0) continue;
      const spec = WEAPONS[g.kind];

      // Engage the in-range target closest to the base. Artillery leads predicted targets for its
      // shell flight; an attack drone's missile guides itself, so it aims at the reported position.
      let best: { id: number; aim: Point; threat: number } | null = null;
      for (const [id, o] of g.orders) {
        const aim =
          g.kind === "attackDrone" ? { x: o.report.x, y: o.report.y } : aimPoint(o.report, o.receivedAt, this.time, spec.flight);
        const d = Math.hypot(aim.x - g.x, aim.y - g.y);
        if (d > spec.range || d < spec.minRange) continue;
        const threat = Math.hypot(aim.x - BASE.x, aim.y - BASE.y);
        if (!best || threat < best.threat) best = { id, aim, threat };
      }
      if (!best) continue;

      const from = this.gunPos(g);
      g.orders.delete(best.id);
      g.angle = Math.atan2(best.aim.y - from.y, best.aim.x - from.x);
      g.lastShot = this.time;
      g.nextFire = this.time + spec.reload;
      this.stats.shellsFired++;
      if (g.kind === "attackDrone") {
        const aim = best.aim;
        const lock = this.enemies
          .map((e) => ({ e, d: Math.hypot(e.x - aim.x, e.y - aim.y) }))
          .filter((x) => x.d <= SEEKER_RADIUS)
          .sort((a, b) => a.d - b.d)[0];
        this.missiles.push({ x: from.x, y: from.y, targetId: lock?.e.id ?? null, to: aim });
      } else {
        this.shells.push({ gun: spec, from, to: best.aim, launchedAt: this.time });
      }
    }
  }

  private impacts(): void {
    const flying: Shell[] = [];
    for (const s of this.shells) {
      if (this.time - s.launchedAt < s.gun.flight) {
        flying.push(s);
        continue;
      }
      this.detonate(s.to, s.gun);
    }
    this.shells = flying;
  }

  private flyMissiles(dt: number): void {
    const spec = WEAPONS.attackDrone;
    const flying: Missile[] = [];
    for (const m of this.missiles) {
      const target = m.targetId === null ? undefined : this.enemies.find((e) => e.id === m.targetId);
      const dest = target ? { x: target.x, y: target.y } : m.to;
      const d = Math.hypot(dest.x - m.x, dest.y - m.y);
      const step = MISSILE_SPEED * dt;
      if (d <= step) {
        this.detonate(dest, spec);
        continue;
      }
      m.x += ((dest.x - m.x) / d) * step;
      m.y += ((dest.y - m.y) / d) * step;
      if (Math.random() < 0.7) {
        this.particles.push({ x: m.x, y: m.y, vx: 0, vy: 0, r: 1.8, born: this.time, life: 0.35, color: "rgba(230,230,230,0.6)" });
      }
      flying.push(m);
    }
    this.missiles = flying;
  }

  private detonate(at: Point, spec: WeaponSpec): void {
    this.burst(at.x, at.y, spec.blast > 40 ? 18 : 10);
    this.craters.push({ x: at.x, y: at.y, r: spec.blast * 0.35 });
    if (this.craters.length > 80) this.craters.shift();
    for (const e of this.enemies) {
      if (Math.hypot(e.x - at.x, e.y - at.y) > spec.blast + ENEMIES[e.kind].radius) continue;
      e.hp -= spec.damage * (e.kind === "armor" ? spec.armorFactor : 1);
    }
    const dead = this.enemies.filter((e) => e.hp <= 0);
    for (const e of dead) this.kill(e);
    if (dead.length) this.enemies = this.enemies.filter((e) => e.hp > 0);
  }

  private kill(e: Enemy): void {
    this.flow.forget(e.id);
    const spec = ENEMIES[e.kind];
    this.credits += spec.reward;
    if (e.kind === "infantry") this.stats.infantryKilled++;
    else if (e.kind === "vehicle") this.stats.vehiclesKilled++;
    else this.stats.armorKilled++;
    this.popup(e.x, e.y - 14, `+${GROUND_POINTS[e.kind]}`, "#7dff9a");
    this.popup(e.x, e.y + 2, `◈ +${spec.reward}`, "#ffcf6b");
    this.burst(e.x, e.y, e.kind === "armor" ? 22 : 10);
  }

  private checkWave(): void {
    if (this.phase === "wave" && this.spawnQueue.length === 0 && this.enemies.length === 0) {
      this.stats.wavesCleared++;
      const bonus = 30 + 5 * this.wave;
      this.credits += bonus;
      this.events.onMessage(`Wave ${this.wave} cleared. +${bonus} credits. Next wave in ${WAVE_BREAK_S}s.`);
      this.phase = "break";
      this.breakEnds = this.time + WAVE_BREAK_S;
    } else if (this.phase === "break" && this.time >= this.breakEnds) {
      this.startWave(this.wave + 1);
    }
  }

  private burst(x: number, y: number, n: number): void {
    for (let i = 0; i < n; i++) {
      this.particles.push({
        x,
        y,
        vx: rand(-60, 60),
        vy: rand(-60, 60),
        r: rand(2, 4.5),
        born: this.time,
        life: rand(0.25, 0.6),
        color: ["#ffd36b", "#ff8a3d", "#ff5d3d", "#fff3c4"][Math.floor(Math.random() * 4)]!,
      });
    }
    for (let i = 0; i < n / 2; i++) {
      this.particles.push({
        x: x + rand(-6, 6),
        y: y + rand(-6, 6),
        vx: rand(-10, 10),
        vy: rand(-10, 10),
        r: rand(6, 11),
        born: this.time,
        life: rand(0.8, 1.6),
        color: "rgba(60,58,54,0.55)",
      });
    }
  }

  private popup(x: number, y: number, text: string, color: string): void {
    this.popups.push({ x, y, text, color, at: this.time });
  }

  private updateEffects(dt: number): void {
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.94;
      p.vy *= 0.94;
    }
    this.particles = this.particles.filter((p) => this.time - p.born < p.life);
    this.popups = this.popups.filter((p) => this.time - p.at < 1.2);
    this.markers = this.markers.filter((m) => this.time - m.at < 0.9);
  }

  private emitHud(): void {
    this.events.onHud({
      phase: this.phase,
      wave: this.wave,
      hp: this.hp,
      credits: this.credits,
      score: scoreGround(this.stats),
      breakLeft: this.phase === "break" ? Math.max(0, this.breakEnds - this.time) : 0,
    });
  }

  // ---------------------------------------------------------------- rendering

  private render(): void {
    const c = this.ctx;
    c.drawImage(this.terrain, 0, 0, WIDTH, HEIGHT);

    c.fillStyle = "rgba(30,24,18,0.45)";
    for (const cr of this.craters) {
      c.beginPath();
      c.arc(cr.x, cr.y, cr.r, 0, Math.PI * 2);
      c.fill();
    }

    for (const g of this.guns) this.drawGunRange(g);
    for (const s of this.sensors) this.drawCoverage(s, s === this.selected ? 0.22 : 0.1);
    if (this.placing && this.hover) this.drawPlacementPreview(this.placing, this.hover);

    for (const m of this.markers) this.drawMarker(m);
    for (const e of this.enemies) this.drawEnemy(e);
    this.drawBase();
    for (const g of this.guns) this.drawGun(g);
    for (const s of this.sensors) this.drawSensor(s);
    for (const s of this.shells) this.drawShell(s);
    for (const m of this.missiles) this.drawMissile(m);

    for (const p of this.particles) {
      const age = (this.time - p.born) / p.life;
      c.globalAlpha = Math.max(0, 1 - age);
      c.fillStyle = p.color;
      c.beginPath();
      c.arc(p.x, p.y, p.r * (1 + age * 0.6), 0, Math.PI * 2);
      c.fill();
    }
    c.globalAlpha = 1;

    c.font = "bold 14px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    for (const p of this.popups) {
      const age = this.time - p.at;
      c.globalAlpha = Math.max(0, 1 - age / 1.2);
      c.fillStyle = p.color;
      c.fillText(p.text, p.x, p.y - age * 24);
    }
    c.globalAlpha = 1;
  }

  private drawGunRange(g: Gun): void {
    this.drawRangeRing(g.kind, { x: g.x, y: g.y }, g === this.selected ? 0.5 : 0.22);
  }

  private drawRangeRing(kind: GunKind, p: Point, alpha: number): void {
    const c = this.ctx;
    c.setLineDash([4, 6]);
    c.strokeStyle = kind === "attackDrone" ? `rgba(255,140,90,${alpha})` : `rgba(255,207,107,${alpha})`;
    c.lineWidth = 1;
    c.beginPath();
    c.arc(p.x, p.y, WEAPONS[kind].range, 0, Math.PI * 2);
    c.stroke();
    c.setLineDash([]);
  }

  private drawCoverage(s: Sensor, alpha: number): void {
    this.drawCoverageAt(s.kind, this.sensorPos(s), s.dir, alpha);
  }

  private drawCoverageAt(kind: SensorKind, p: Point, dir: number, alpha: number): void {
    const c = this.ctx;
    const color = SENSOR_COLORS[kind];
    const spec = SENSORS[kind];
    c.fillStyle = hexAlpha(color, alpha);
    c.strokeStyle = hexAlpha(color, alpha * 3);
    c.lineWidth = 1;
    c.beginPath();
    if (kind === "camera") {
      c.moveTo(p.x, p.y);
      c.arc(p.x, p.y, spec.range, dir - CAMERA_FOV / 2, dir + CAMERA_FOV / 2);
      c.closePath();
    } else {
      c.arc(p.x, p.y, spec.range, 0, Math.PI * 2);
    }
    c.fill();
    c.stroke();
    if (kind === "acoustic") {
      c.setLineDash([3, 4]);
      c.beginPath();
      c.arc(p.x, p.y, ACOUSTIC_INFANTRY_RANGE, 0, Math.PI * 2);
      c.stroke();
      c.setLineDash([]);
    }
  }

  private drawPlacementPreview(kind: PlaceKind, p: Point): void {
    const ok = this.placementProblem(kind, p) === null;
    if (isSensorKind(kind)) this.drawCoverageAt(kind, p, Math.PI, ok ? 0.14 : 0.05);
    else this.drawRangeRing(kind, p, ok ? 0.7 : 0.25);
    const c = this.ctx;
    c.strokeStyle = ok ? "#ffffff" : "#ff6b6b";
    c.lineWidth = 2;
    c.beginPath();
    c.arc(p.x, p.y, 10, 0, Math.PI * 2);
    c.stroke();
    if (kind === "drone" || kind === "attackDrone") {
      c.setLineDash([2, 4]);
      c.beginPath();
      c.arc(p.x, p.y, kind === "drone" ? DRONE_ORBIT : ATTACK_DRONE_ORBIT, 0, Math.PI * 2);
      c.stroke();
      c.setLineDash([]);
    }
  }

  private drawMarker(m: Marker): void {
    const c = this.ctx;
    const k = (this.time - m.at) / 0.9;
    const color = SENSOR_COLORS[m.kind];
    c.strokeStyle = hexAlpha(color, 0.9 * (1 - k));
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(m.x - 5, m.y);
    c.lineTo(m.x + 5, m.y);
    c.moveTo(m.x, m.y - 5);
    c.lineTo(m.x, m.y + 5);
    c.stroke();
    if (m.error > 10) {
      c.setLineDash([2, 3]);
      c.beginPath();
      c.arc(m.x, m.y, m.error, 0, Math.PI * 2);
      c.stroke();
      c.setLineDash([]);
    }
  }

  private drawEnemy(e: Enemy): void {
    const c = this.ctx;
    c.save();
    c.translate(e.x, e.y);
    if (e.kind === "infantry") {
      c.fillStyle = "#c4513f";
      c.strokeStyle = "#3a120c";
      c.lineWidth = 1;
      for (const [dx, dy] of [
        [0, -4],
        [-4, 3],
        [4, 3],
      ] as const) {
        c.beginPath();
        c.arc(dx, dy, 2.6, 0, Math.PI * 2);
        c.fill();
        c.stroke();
      }
    } else {
      c.rotate(e.heading);
      if (e.kind === "vehicle") {
        c.fillStyle = "#2b2420";
        c.fillRect(-9, -7, 4, 14);
        c.fillRect(4, -7, 4, 14);
        c.fillStyle = "#b04a39";
        c.fillRect(-10, -5, 20, 10);
        c.fillStyle = "#e7b9a8";
        c.fillRect(5, -3.5, 4, 7);
      } else {
        c.fillStyle = "#2b2420";
        c.fillRect(-13, -10, 26, 4);
        c.fillRect(-13, 6, 26, 4);
        c.fillStyle = "#8f3a2d";
        c.fillRect(-12, -7, 24, 14);
        c.fillStyle = "#6b2a20";
        c.beginPath();
        c.arc(-1, 0, 5.5, 0, Math.PI * 2);
        c.fill();
        c.fillRect(2, -1.5, 15, 3);
      }
    }
    c.restore();

    if (e.hp < e.maxHp) {
      const w = 18;
      c.fillStyle = "rgba(0,0,0,0.6)";
      c.fillRect(e.x - w / 2, e.y - 16, w, 3);
      c.fillStyle = "#7dff9a";
      c.fillRect(e.x - w / 2, e.y - 16, (w * Math.max(0, e.hp)) / e.maxHp, 3);
    }
  }

  private drawBase(): void {
    const c = this.ctx;
    c.fillStyle = "#4c5a4e";
    c.strokeStyle = "#c7b98f";
    c.lineWidth = 2;
    c.beginPath();
    c.rect(BASE.x - 22, BASE.y - 22, 44, 44);
    c.fill();
    c.stroke();
    c.fillStyle = "#2f3a31";
    c.fillRect(BASE.x - 12, BASE.y - 12, 24, 24);
    c.fillStyle = "#7dff9a";
    c.font = "bold 10px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    c.fillText("BASE", BASE.x, BASE.y + 4);
    const w = 44;
    c.fillStyle = "rgba(0,0,0,0.6)";
    c.fillRect(BASE.x - w / 2, BASE.y - 32, w, 5);
    c.fillStyle = this.hp > BASE_HP / 3 ? "#7dff9a" : "#ff6b6b";
    c.fillRect(BASE.x - w / 2, BASE.y - 32, (w * this.hp) / BASE_HP, 5);
  }

  private drawGun(g: Gun): void {
    if (g.kind === "attackDrone") {
      this.drawAttackDrone(g);
      return;
    }
    const c = this.ctx;
    const pos = { x: g.x, y: g.y };
    const big = g.kind === "howitzer";
    c.fillStyle = "#8a7f5f";
    c.beginPath();
    c.arc(pos.x, pos.y, big ? 17 : 14, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#3d4440";
    c.beginPath();
    c.arc(pos.x, pos.y, big ? 10 : 8, 0, Math.PI * 2);
    c.fill();
    c.save();
    c.translate(pos.x, pos.y);
    c.rotate(g.angle);
    c.fillStyle = "#596158";
    c.fillRect(0, -2.5, big ? 24 : 15, 5);
    if (this.time - g.lastShot < 0.08) {
      c.fillStyle = "#ffe08a";
      c.beginPath();
      c.arc(big ? 27 : 18, 0, 6, 0, Math.PI * 2);
      c.fill();
    }
    c.restore();
    // Reload bar
    this.drawReload(g, pos, big ? 20 : 17);
    this.drawGunLabel(g, pos, big ? 34 : 31);
  }

  private drawReload(g: Gun, pos: Point, dy: number): void {
    const c = this.ctx;
    const k = Math.min(1, 1 - (g.nextFire - this.time) / WEAPONS[g.kind].reload);
    c.fillStyle = "rgba(0,0,0,0.55)";
    c.fillRect(pos.x - 14, pos.y + dy, 28, 3);
    c.fillStyle = g.kind === "attackDrone" ? "#ff8c5a" : "#ffcf6b";
    c.fillRect(pos.x - 14, pos.y + dy, 28 * Math.max(0, k), 3);
  }

  private drawGunLabel(g: Gun, pos: Point, dy: number): void {
    const c = this.ctx;
    c.fillStyle = g.kind === "attackDrone" ? "#ff8c5a" : "#ffcf6b";
    c.font = g.fixed ? "bold 10px ui-monospace, Menlo, monospace" : "10px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    c.fillText(g.fixed ? g.label.toUpperCase() : g.label, pos.x, pos.y + dy);
    if (g === this.selected) {
      c.strokeStyle = "#ffffff";
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(g.x, g.y, g.kind === "attackDrone" ? ATTACK_DRONE_ORBIT + 8 : 20, 0, Math.PI * 2);
      c.stroke();
    }
  }

  private drawAttackDrone(g: Gun): void {
    const c = this.ctx;
    const color = "#ff8c5a";
    c.strokeStyle = hexAlpha(color, 0.35);
    c.lineWidth = 1;
    c.setLineDash([2, 4]);
    c.beginPath();
    c.arc(g.x, g.y, ATTACK_DRONE_ORBIT, 0, Math.PI * 2);
    c.stroke();
    c.setLineDash([]);
    // Station marker
    c.fillStyle = hexAlpha(color, 0.5);
    c.beginPath();
    c.arc(g.x, g.y, 3, 0, Math.PI * 2);
    c.fill();

    const p = this.gunPos(g);
    const a = g.phase + (this.time * Math.PI * 2) / ATTACK_DRONE_PERIOD_S + Math.PI / 2; // heading along the orbit
    c.save();
    c.translate(p.x, p.y);
    c.rotate(a);
    c.fillStyle = color;
    c.beginPath(); // delta wing
    c.moveTo(10, 0);
    c.lineTo(-7, -8);
    c.lineTo(-4, 0);
    c.lineTo(-7, 8);
    c.closePath();
    c.fill();
    c.restore();
    if (this.time - g.lastShot < 0.1) {
      c.fillStyle = "#fff3c4";
      c.beginPath();
      c.arc(p.x, p.y, 5, 0, Math.PI * 2);
      c.fill();
    }
    this.drawReload(g, { x: g.x, y: g.y }, ATTACK_DRONE_ORBIT + 6);
    this.drawGunLabel(g, { x: g.x, y: g.y }, ATTACK_DRONE_ORBIT + 20);
  }

  private drawMissile(m: Missile): void {
    const c = this.ctx;
    c.fillStyle = "#fff3c4";
    c.beginPath();
    c.arc(m.x, m.y, 2.4, 0, Math.PI * 2);
    c.fill();
  }

  private drawSensor(s: Sensor): void {
    const c = this.ctx;
    const color = SENSOR_COLORS[s.kind];
    const p = this.sensorPos(s);
    if (s.kind === "drone") {
      c.strokeStyle = hexAlpha(color, 0.35);
      c.setLineDash([2, 4]);
      c.beginPath();
      c.arc(s.x, s.y, DRONE_ORBIT, 0, Math.PI * 2);
      c.stroke();
      c.setLineDash([]);
      c.strokeStyle = color;
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(p.x - 7, p.y - 7);
      c.lineTo(p.x + 7, p.y + 7);
      c.moveTo(p.x + 7, p.y - 7);
      c.lineTo(p.x - 7, p.y + 7);
      c.stroke();
      c.fillStyle = color;
      for (const [dx, dy] of [
        [-7, -7],
        [7, 7],
        [7, -7],
        [-7, 7],
      ] as const) {
        c.beginPath();
        c.arc(p.x + dx, p.y + dy, 3, 0, Math.PI * 2);
        c.fill();
      }
    } else {
      c.fillStyle = "#1b2430";
      c.strokeStyle = color;
      c.lineWidth = 2;
      c.beginPath();
      c.arc(p.x, p.y, 8, 0, Math.PI * 2);
      c.fill();
      c.stroke();
      c.fillStyle = color;
      if (s.kind === "camera") {
        c.save();
        c.translate(p.x, p.y);
        c.rotate(s.dir);
        c.fillRect(-1, -3, 10, 6);
        c.restore();
      } else {
        for (let i = 1; i <= 2; i++) {
          c.strokeStyle = hexAlpha(color, 0.6 / i);
          c.beginPath();
          c.arc(p.x, p.y, 8 + i * 4 + ((this.time * 8) % 4), 0, Math.PI * 2);
          c.stroke();
        }
      }
    }
    if (s === this.selected) {
      c.strokeStyle = "#ffffff";
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(s.x, s.y, 14, 0, Math.PI * 2);
      c.stroke();
    }
    c.fillStyle = color;
    c.font = "10px ui-monospace, Menlo, monospace";
    c.textAlign = "center";
    c.fillText(s.label, s.x, s.y + (s.kind === "drone" ? DRONE_ORBIT + 12 : 20));
  }

  private drawShell(s: Shell): void {
    const c = this.ctx;
    const k = Math.min(1, (this.time - s.launchedAt) / s.gun.flight);
    const x = s.from.x + (s.to.x - s.from.x) * k;
    const y = s.from.y + (s.to.y - s.from.y) * k;
    const h = Math.sin(Math.PI * k) * (s.gun.kind === "howitzer" ? 70 : 50);
    c.fillStyle = "rgba(0,0,0,0.35)";
    c.beginPath();
    c.ellipse(x, y, 3, 2, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "#fff1b8";
    c.beginPath();
    c.arc(x, y - h, s.gun.kind === "howitzer" ? 3 : 2.2, 0, Math.PI * 2);
    c.fill();
    // Impact marker so players can see where the gun aimed.
    c.strokeStyle = `rgba(255,207,107,${0.25 + 0.4 * k})`;
    c.lineWidth = 1;
    c.beginPath();
    c.arc(s.to.x, s.to.y, s.gun.blast, 0, Math.PI * 2);
    c.stroke();
  }
}

function article(word: string): string {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

function describeWave(s: Record<EnemyKind, number>): string {
  const parts = [`${s.infantry} infantry`];
  if (s.vehicle) parts.push(`${s.vehicle} vehicle${s.vehicle > 1 ? "s" : ""}`);
  if (s.armor) parts.push(`${s.armor} tank${s.armor > 1 ? "s" : ""}`);
  return parts.join(", ");
}

function hexAlpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.max(0, Math.min(1, a))})`;
}

/** Grass, scattered trees and the roads, painted once. */
function paintTerrain(): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = WIDTH * 2;
  cv.height = HEIGHT * 2;
  const c = cv.getContext("2d")!;
  c.scale(2, 2);

  c.fillStyle = "#2f4a2c";
  c.fillRect(0, 0, WIDTH, HEIGHT);
  for (let i = 0; i < 260; i++) {
    c.fillStyle = Math.random() < 0.5 ? "rgba(70,100,55,0.25)" : "rgba(25,40,22,0.25)";
    c.beginPath();
    c.arc(Math.random() * WIDTH, Math.random() * HEIGHT, rand(10, 40), 0, Math.PI * 2);
    c.fill();
  }

  const roads = Object.values(ROADS);
  for (let i = 0; i < 340; i++) {
    const p = { x: Math.random() * WIDTH, y: Math.random() * HEIGHT };
    if (roads.some((r) => distToRoad(p, r) < 22)) continue;
    if (FIXED_GUNS.some((g) => Math.hypot(p.x - g.pos.x, p.y - g.pos.y) < 30)) continue;
    if (Math.hypot(p.x - BASE.x, p.y - BASE.y) < 40) continue;
    const r = rand(5, 10);
    c.fillStyle = "rgba(0,0,0,0.25)";
    c.beginPath();
    c.arc(p.x + 2, p.y + 2, r, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = Math.random() < 0.5 ? "#21391f" : "#284526";
    c.beginPath();
    c.arc(p.x, p.y, r, 0, Math.PI * 2);
    c.fill();
  }

  const stroke = (pts: Point[], width: number, color: string, dash: number[] = []) => {
    c.strokeStyle = color;
    c.lineWidth = width;
    c.lineCap = "round";
    c.lineJoin = "round";
    c.setLineDash(dash);
    c.beginPath();
    pts.forEach((p, i) => (i ? c.lineTo(p.x, p.y) : c.moveTo(p.x, p.y)));
    c.stroke();
    c.setLineDash([]);
  };
  for (const road of [ROADS.north, ROADS.south]) {
    stroke(road, 26, "#5b4e38");
    stroke(road, 20, "#7d6c4d");
    stroke(road, 1.5, "rgba(230,215,170,0.35)", [10, 12]);
  }
  stroke(ROADS.trail, 8, "rgba(120,100,70,0.75)", [3, 5]);
  return cv;
}
